import {
  isForbidden,
  useConnectPipelineRepo,
  useExportPipelineRevision,
  usePipelineGitConnection,
} from "@via/api/pipeline-git.api.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { useIntegrationTokens } from "@via/api/integration-tokens.api.tsx";
import { PipelineGitProvider } from "common";
import { JSX, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

/** A folder name the server accepts: lowercase letters, digits and dashes. */
export const folderFromName = (name: string | undefined): string =>
  (name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 200);

/**
 * The pipeline page's Git tab: connect the pipeline to the https repository it publishes to, and
 * export the saved revision, with every process it uses, as the next tagged revision there.
 *
 * Copied from the process Git panel (`create-edit-process-form/script-assets.tsx`). The URL,
 * folder and provider are fixed once connected, because the tags other installations import from
 * live there; branch and credential stay editable. The connection GET answers 403 to anyone who
 * cannot write the pipeline, so that case is a sentence, not an empty connect form.
 *
 * The stored credential is reported by id only. When it is not one of the viewer's own
 * credentials, the select still shows it ("not one of yours") and an update resends it, so
 * changing the branch never silently drops a credential someone else chose.
 */
export default function PipelineGit(): JSX.Element {
  const { pipelineId: pipelineIdParam } = useParams();
  const pipelineId = Number(pipelineIdParam);
  const { pipelineDraft } = useGlobal();
  const canEdit = Boolean(pipelineDraft?.isEditingAllowed);

  const { data, isLoading, isError, error } =
    usePipelineGitConnection(pipelineId);
  // Only an object is a connection. An empty body arrives as "" (#1256), and reading that as
  // connected showed an unconnected pipeline the connected view with no repository in it.
  const connection =
    undefined === data || "object" === typeof data ? data : null;
  const connectRepo = useConnectPipelineRepo(pipelineId);
  const exportRevision = useExportPipelineRevision(pipelineId);
  const isConnected = undefined !== connection && null !== connection;

  const [repoUrl, setRepoUrl] = useState("");
  const [repoPath, setRepoPath] = useState("");
  const [provider, setProvider] = useState<PipelineGitProvider>("github");
  const [defaultBranch, setDefaultBranch] = useState("main");
  // "" is "no credential"; a digit string names one.
  const [serviceTokenId, setServiceTokenId] = useState("");

  // Seed once from what the server holds, so a refetch never overwrites what the user is typing.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || undefined === connection) {
      return;
    }
    seeded.current = true;
    if (null === connection) {
      setRepoPath(folderFromName(pipelineDraft?.name));
      return;
    }
    setRepoUrl(connection.repoUrl);
    setRepoPath(connection.repoPath);
    setProvider(connection.provider as PipelineGitProvider);
    setDefaultBranch(connection.defaultBranch);
    setServiceTokenId(
      null === connection.serviceTokenId
        ? ""
        : String(connection.serviceTokenId)
    );
  }, [connection, pipelineDraft?.name]);

  const { data: credentialPage } = useIntegrationTokens(0, 100);
  const matchingCredentials = useMemo(
    () =>
      (credentialPage?.data ?? [])
        .filter(credential => credential.tokenType === provider)
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ),
    [credentialPage, provider]
  );
  const missingCredentialId =
    isConnected &&
    null !== connection.serviceTokenId &&
    !matchingCredentials.some(
      credential => credential.id === connection.serviceTokenId
    )
      ? connection.serviceTokenId
      : null;

  const exported = exportRevision.data;

  if (isLoading) {
    return (
      <p className="p-4 text-sm text-gray-500">
        Loading the repository connection...
      </p>
    );
  }
  if (isError) {
    return (
      <p className="p-4 text-sm text-red-600" data-testid="pipelineGitError">
        {isForbidden(error)
          ? "You need write permission on this pipeline to manage its repository."
          : "The repository connection could not be read. Reload the page and try again."}
      </p>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="rounded-md border border-gray-200 p-3">
        <h3 className="text-sm font-semibold text-gray-900">Git repository</h3>
        <p className="mt-1 text-sm text-gray-500">
          {isConnected
            ? "The repository and folder are fixed once set, because published revision tags live there. The branch and the credential can still be changed."
            : "Publish this pipeline, with the exact revisions of the processes it uses, to an https repository. Another installation can then import it from there."}
        </p>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
          {isConnected ? (
            <div className="sm:col-span-2">
              <p className="text-xs text-gray-500">Connected to</p>
              <a
                href={connection.repoUrl}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-sm break-all text-indigo-600 hover:underline">
                {connection.repoUrl}
              </a>
              <p className="mt-1 text-xs text-gray-500">
                publishes to{" "}
                <span className="font-mono">
                  pipelines/{connection.repoPath}/
                </span>
              </p>
            </div>
          ) : (
            <>
              <input
                className="rounded border border-gray-300 px-2 py-1 text-sm sm:col-span-2"
                placeholder="https://github.com/owner/repo.git"
                value={repoUrl}
                aria-label="Repository URL"
                onChange={event => setRepoUrl(event.target.value)}
              />
              <input
                className="rounded border border-gray-300 px-2 py-1 text-sm sm:col-span-2"
                placeholder="pipeline-folder"
                value={repoPath}
                aria-label="Pipeline folder"
                onChange={event => setRepoPath(event.target.value)}
              />
            </>
          )}
          {isConnected ? (
            <div>
              <p className="text-xs text-gray-500">Provider</p>
              <p className="text-sm text-gray-700">{connection.provider}</p>
            </div>
          ) : (
            <select
              className="rounded border border-gray-300 px-2 py-1 text-sm"
              value={provider}
              aria-label="Provider"
              onChange={event => {
                // A credential chosen for the old host never travels with the new one.
                setProvider(event.target.value as PipelineGitProvider);
                setServiceTokenId("");
              }}>
              <option value="github">github</option>
              <option value="gitlab">gitlab</option>
              <option value="bitbucket">bitbucket</option>
            </select>
          )}
          <input
            className="rounded border border-gray-300 px-2 py-1 text-sm"
            placeholder="main"
            value={defaultBranch}
            aria-label="Default branch"
            onChange={event => setDefaultBranch(event.target.value)}
          />
          <div>
            <select
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100"
              value={serviceTokenId}
              aria-label="Credential"
              disabled={
                0 === matchingCredentials.length && null === missingCredentialId
              }
              onChange={event => setServiceTokenId(event.target.value)}>
              <option value="">No credential (public repository)</option>
              {matchingCredentials.map(credential => (
                <option key={credential.id} value={String(credential.id)}>
                  {credential.name}
                </option>
              ))}
              {null !== missingCredentialId ? (
                <option value={String(missingCredentialId)}>
                  {`Credential ${missingCredentialId} (not one of yours)`}
                </option>
              ) : null}
            </select>
            {0 === matchingCredentials.length &&
            null === missingCredentialId ? (
              <p className="mt-1 text-xs text-gray-500">
                Add a {provider} credential on the integrations page to push to
                a private repository.
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            data-testid="pipelineGitConnect"
            disabled={!canEdit || connectRepo.isPending}
            onClick={() =>
              connectRepo.mutate({
                repoUrl: isConnected ? connection.repoUrl : repoUrl.trim(),
                repoPath: isConnected ? connection.repoPath : repoPath.trim(),
                provider: isConnected
                  ? (connection.provider as PipelineGitProvider)
                  : provider,
                defaultBranch: defaultBranch.trim(),
                serviceTokenId:
                  "" === serviceTokenId ? null : Number(serviceTokenId),
              })
            }
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:text-gray-400">
            {connectRepo.isPending
              ? "Saving..."
              : isConnected
                ? "Update connection"
                : "Connect"}
          </button>
          <button
            type="button"
            data-testid="pipelineGitExport"
            disabled={!canEdit || !isConnected || exportRevision.isPending}
            onClick={() => exportRevision.mutate()}
            className="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:bg-gray-300">
            {exportRevision.isPending ? "Exporting..." : "Export next revision"}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Export publishes the last saved revision of this pipeline. Save your
          changes first.
        </p>

        {undefined !== exported ? (
          <div
            className="mt-3 rounded bg-gray-50 p-3 text-sm"
            data-testid="pipelineGitExportResult">
            {exported.committed ? (
              <p className="font-medium text-green-700">
                Published as {exported.tag}.
              </p>
            ) : (
              <p className="font-medium text-gray-700">
                Nothing changed. The repository already holds this revision as{" "}
                {exported.tag}.
              </p>
            )}
            {0 < exported.processes.length ? (
              <>
                <p className="mt-2 text-xs text-gray-500">Processes</p>
                <ul className="list-inside list-disc font-mono text-xs text-gray-600">
                  {exported.processes.map(process => (
                    <li key={process.repoPath}>
                      {process.tag}
                      {process.written ? "" : " (already published)"}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {0 < exported.modules.length ? (
              <>
                <p className="mt-2 text-xs text-gray-500">Modules</p>
                <ul className="list-inside list-disc font-mono text-xs text-gray-600">
                  {exported.modules.map(module => (
                    <li key={module.slug}>
                      {module.tag}
                      {module.written ? "" : " (already published)"}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
